import React, { useCallback, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { stepUpHeaders } from "../../gateway/api";
import { AFFILIATE_PATHS, REFERRAL_STATES } from "../../affiliate/contract";
import { useT } from "../../lib/LanguageContext";
import { formatMoneyCode } from "../../lib/formatMoney";
import useAdminLoad, { payloadShapeError } from "../../hooks/useAdminLoad";
import LoadFailure from "../../components/ui/LoadFailure";
import StepUpConfirmDialog from "../../gateway/components/StepUpConfirmDialog";
import { affiliateErrorText } from "../../admin/affiliate/errorText";
import { apiErrorCode } from "../../lib/apiErrors";
import { ACCOUNT_STATE_KEYS } from "./AffiliatesTable";
import CommissionsTable from "./CommissionsTable";
import { useScreenWrites } from "../screenWrites";

/**
 * One affiliate: the console's two aggregate blocks scoped to them, plus their commissions.
 *
 * DISABLING IS FORWARD-ONLY and the dialog says so. It stops new attributions and new
 * accruals; it reverses nothing and freezes nothing already accrued. Taking money back is
 * `POST /commissions/{id}/reverse`, one commission at a time, with a recorded reason —
 * precisely so that a status flip can never be mistaken for a clawback.
 */
/**
 * One tile per referral state, in `REFERRAL_STATES` order, plus the total — shared with
 * the console's Overview so both screens name the same counts the same way. The tiles
 * PARTITION the referrals: each label says which state it counts, and only the total is
 * "signed up", because every referral in every state signed up.
 */
export const REFERRAL_TILE_KEYS = Object.freeze({
  linked: "saff.overview.linked",
  active: "saff.overview.active",
  expired: "saff.overview.expired",
  voided: "saff.overview.voided",
});

/**
 * True when `referrals` carries a non-negative integer count for EVERY `REFERRAL_STATES`
 * entry. Anything short of that is a payload-shape error, never a tile quietly left out:
 * the total is the sum of the states, so skipping one would undercount it (SHEET-232).
 */
export function isReferralCounts(referrals) {
  return !!referrals && typeof referrals === "object"
    && REFERRAL_STATES.every((s) => Number.isInteger(referrals[s]) && referrals[s] >= 0);
}

/** Every referral state, and their sum. Only call on a block `isReferralCounts` accepted. */
export function referralTiles(referrals) {
  return { states: REFERRAL_STATES, total: REFERRAL_STATES.reduce((n, s) => n + referrals[s], 0) };
}

function Block({ label, value, testId }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid={testId}>
      <div className="text-2xl text-slate-800 tabular-nums">{value}</div>
      <div className="text-xs text-slate-400">{label}</div>
    </div>
  );
}

export default function AffiliateDetail({ tenantId, onBack, gatewayApi }) {
  // Every affiliate write is finance.write behind a step-up (minted with finance.write too).
  const { manage: canManage } = useScreenWrites("affiliate");
  const t = useT("superadmin");
  const tv = useT("common");
  const [detail, setDetail] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [refusal, setRefusal] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get(AFFILIATE_PATHS.adminAffiliate(tenantId));
    if (!data || typeof data !== "object" || !data.affiliate
        || typeof data.affiliate !== "object" || !isReferralCounts(data.referrals) || !data.commissions) {
      throw payloadShapeError();
    }
    setDetail(data);
  }, [tenantId]);
  const { loadError, reload } = useAdminLoad(load);

  const nextStatus = detail?.affiliate?.status === "active" ? "disabled" : "active";

  const confirm = async ({ stepUpToken }) => {
    setRefusal("");
    try {
      const { data } = await api.put(
        AFFILIATE_PATHS.adminAffiliateStatus(tenantId),
        { status: nextStatus },
        stepUpHeaders(stepUpToken),
      );
      setDetail((d) => (d ? { ...d, affiliate: data } : d));
      toast.success(t("saff.detail.statusChanged"));
      return { ok: true };
    } catch (e) {
      const code = apiErrorCode(e);
      if (code === "REAUTH_REQUIRED") return { ok: false, code };
      setRefusal(affiliateErrorText(e, { t, tv, prefix: "saff", fallback: tv("errors.saveFailed") }));
      return { ok: false, code: null };
    }
  };

  return (
    <div className="space-y-6" data-testid="sa-affiliate-detail">
      <button
        type="button"
        onClick={onBack}
        data-testid="sa-affiliate-detail-back"
        className="text-sm text-slate-500 inline-flex items-center gap-1"
      >
        <ArrowLeft className="w-4 h-4" /> {t("saff.detail.back")}
      </button>

      {loadError ? (
        <LoadFailure
          message={affiliateErrorText(loadError, { t, tv, prefix: "saff", fallback: tv("errors.loadFailed") })}
          onRetry={reload}
          retryLabel={tv("actions.retry")}
          testId="sa-affiliate-detail-load-error"
          retryTestId="sa-affiliate-detail-retry"
        />
      ) : !detail ? (
        <p className="text-slate-400 text-sm">{t("saff.loading")}</p>
      ) : (
        <>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-2xl text-slate-800">
                {detail.affiliate.practiceName || detail.affiliate.tenantId}
              </h2>
              <p className="text-sm text-slate-400 font-mono mt-0.5">{detail.affiliate.code}</p>
              <p className="text-xs text-slate-400 mt-1" data-testid="sa-affiliate-detail-status">
                {t(ACCOUNT_STATE_KEYS[detail.affiliate.status] || "saff.accountState.active")}
              </p>
            </div>
            {canManage && <button
              type="button"
              onClick={() => setConfirming(true)}
              data-testid="sa-affiliate-toggle-status"
              className="text-sm px-4 py-2 rounded-xl border border-slate-200"
            >
              {nextStatus === "disabled" ? t("saff.detail.disable") : t("saff.detail.enable")}
            </button>}
          </div>

          {refusal && (
            <p className="text-xs text-rose-600" role="alert" data-testid="sa-affiliate-detail-error">{refusal}</p>
          )}

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Block
              label={t("saff.overview.referralsTotal")}
              value={referralTiles(detail.referrals).total}
              testId="sa-detail-referrals-total"
            />
            {referralTiles(detail.referrals).states.map((s) => (
              <Block key={s} label={t(REFERRAL_TILE_KEYS[s])} value={detail.referrals[s]} testId={`sa-detail-${s}`} />
            ))}
            {/* `clicksTotal` is LIFETIME, not the console's 30-day figure — labelled with
                the affiliates table's own column so the number and its name agree. */}
            <Block
              label={t("saff.affiliates.colClicks")}
              value={detail.affiliate.clicksTotal}
              testId="sa-detail-clicks"
            />
            <Block
              label={t("saff.overview.accrued")}
              value={formatMoneyCode(detail.commissions.accruedCents, detail.commissions.currency)}
              testId="sa-detail-accrued"
            />
            <Block
              label={t("saff.overview.onHold")}
              value={formatMoneyCode(detail.commissions.onHoldCents, detail.commissions.currency)}
              testId="sa-detail-onhold"
            />
            <Block
              label={t("saff.overview.reversed")}
              value={formatMoneyCode(detail.commissions.reversedCents, detail.commissions.currency)}
              testId="sa-detail-reversed"
            />
          </div>

          <CommissionsTable gatewayApi={gatewayApi} affiliateTenantId={tenantId} />

          {canManage && <StepUpConfirmDialog
            open={confirming}
            onClose={(didMutate) => { setConfirming(false); setRefusal(""); if (didMutate) reload(); }}
            titleKey={nextStatus === "disabled" ? "saff.detail.confirmDisableTitle" : "saff.detail.confirmEnableTitle"}
            descKey="saff.detail.confirmDesc"
            requireAck
            ackKey="saff.detail.ack"
            onConfirm={confirm}
            gatewayApi={gatewayApi}
          />}
        </>
      )}
    </div>
  );
}
